import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, ShieldCheck, Trash2, DoorOpen } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { TalentExitCaseDetail } from '@/components/client/TalentExitCaseDetail';

export default function AdminTalentExit() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const es = i18n.language?.startsWith('es');
  const isAdmin = user?.role === 'hm_admin';
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState('');
  const [cases, setCases] = useState([]);
  const [settings, setSettings] = useState(null);
  const [clientAdmins, setClientAdmins] = useState([]);
  const [grantId, setGrantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api.get('/admin/clients').then((r) => {
      const list = (r.data?.items || r.data || []).filter((c) => c.status !== 'archived');
      setCompanies(list);
      if (list.length && !cid) setCid(list[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [cs, st] = await Promise.all([
        api.get(`/admin/talentexit/${cid}/cases`).then((r) => r.data),
        api.get(`/admin/talentexit/${cid}/settings`).then((r) => r.data),
      ]);
      setCases(cs); setSettings(st);
      if (isAdmin) {
        const eligible = await api.get(`/admin/talentexit/${cid}/eligible-admins`).then((r) => r.data);
        setClientAdmins(eligible);
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [cid, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!grantId) return;
    try {
      await api.post(`/admin/talentexit/${cid}/authorized`, { user_id: grantId });
      toast.success(t('talentexit.admin.granted', { defaultValue: 'Authorization granted.' }));
      setGrantId(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revoke = async (uid) => {
    try {
      await api.delete(`/admin/talentexit/${cid}/authorized/${uid}`);
      toast.success(t('talentexit.admin.revoked', { defaultValue: 'Authorization revoked.' }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen" data-testid="admin-talentexit">
      <Topbar title="TalentExit" subtitle={t('talentexit.admin.subtitle', { defaultValue: 'Exit-process oversight and authorization governance' })} />
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger className="w-full sm:w-96 max-w-full min-w-0 [&>span]:truncate" data-testid="tx-admin-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {settings && (
            <Badge variant="outline" className="shrink-0">{t('talentexit.settings.retention', { defaultValue: 'Retention' })}: {settings.retention_years} {t('talentexit.settings.years', { defaultValue: 'years' }).toLowerCase()}</Badge>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <Card className="xl:col-span-2 min-w-0 shadow-card overflow-hidden">
            <div className="p-4 border-b flex items-center gap-2">
              <DoorOpen className="h-4 w-4 text-warning" />
              <h4 className="text-sm font-semibold">{t('talentexit.admin.cases', { defaultValue: 'Exit cases (read-only)' })}</h4>
            </div>
            {loading ? (
              <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : cases.length === 0 ? (
              <p className="p-8 text-sm text-muted-foreground text-center" data-testid="tx-admin-empty">{t('talentexit.emptyCases', { defaultValue: 'No exit cases yet.' })}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[13px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-3 font-medium">{t('talentexit.fields.employee', { defaultValue: 'Employee' })}</th>
                      <th className="px-4 py-3 font-medium">{t('talentexit.fields.exitType', { defaultValue: 'Exit type' })}</th>
                      <th className="px-4 py-3 font-medium">{t('talentexit.fields.interview', { defaultValue: 'Interview' })}</th>
                      <th className="px-4 py-3 font-medium">{t('talentexit.fields.status', { defaultValue: 'Status' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map((c) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => setDetailId(c.id)} data-testid={`tx-admin-row-${c.id}`}>
                        <td className="px-4 py-3 font-medium">{c.snapshot?.user_name}</td>
                        <td className="px-4 py-3">{t(`talentexit.exitTypes.${c.exit_type}`, { defaultValue: c.exit_type })}</td>
                        <td className="px-4 py-3"><Badge variant="outline">{t(`talentexit.interviewStatus.${c.interview?.status || 'not_started'}`)}</Badge></td>
                        <td className="px-4 py-3"><Badge variant="outline" className={c.status === 'closed' ? 'bg-success/10 text-success border-success/20' : 'bg-info/10 text-info border-info/20'}>{t(`talentexit.status.${c.status}`, { defaultValue: c.status })}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-5 min-w-0 shadow-card space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />{t('talentexit.settings.authorized', { defaultValue: 'Authorized TalentExit users' })}</h4>
            <p className="text-xs text-muted-foreground">{t('talentexit.admin.authorizedHint', { defaultValue: 'Governed authorization list (Founder Q6.1): only these client admins may read raw exit-interview responses or record HR-led interviews. Every change is audited.' })}</p>
            <div className="space-y-2" data-testid="tx-admin-authorized">
              {(settings?.authorized_users || []).map((u) => (
                <div key={u.id} className="flex items-center justify-between rounded border border-border/60 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="text-destructive h-7 px-2" onClick={() => revoke(u.id)} data-testid={`tx-revoke-${u.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {(settings?.authorized_users || []).length === 0 && (
                <p className="text-sm text-muted-foreground">{t('talentexit.settings.noAuthorized', { defaultValue: 'No authorized users yet.' })}</p>
              )}
            </div>
            {isAdmin && (
              <div className="space-y-2 pt-1">
                <Select value={grantId} onValueChange={setGrantId}>
                  <SelectTrigger className="w-full min-w-0" data-testid="tx-grant-select">
                    <SelectValue placeholder={t('talentexit.admin.selectUser', { defaultValue: 'Select client admin' })} />
                  </SelectTrigger>
                  <SelectContent>
                    {clientAdmins.filter((u) => !(settings?.authorized_users || []).some((a) => a.id === u.id)).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="w-full" onClick={grant} disabled={!grantId} data-testid="tx-grant-btn">
                  {t('talentexit.admin.grant', { defaultValue: 'Authorize' })}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {detailId && (
        <TalentExitCaseDetail caseId={detailId} adminMode companyId={cid} onClose={() => { setDetailId(null); load(); }} />
      )}
    </div>
  );
}
